'use client';

/**
 * Customer Auth Provider
 * Context provider for customer authentication state and operations
 * 
 * This is the enhanced version that integrates Supabase auth with Route Royal
 * customer provisioning and profile management.
 * 
 * IMPORTANT: This provider must be used INSIDE a TenantProvider to access
 * the organisation ID. The auth system is multi-tenant aware.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { createCustomerClient, type CustomerAPIClient } from '@/lib/api/clients/customer';
import { useTenantOptional } from '@/contexts/TenantContext';
import type { CustomerProfile, AuthError } from '@/types/customer';
import {
  setOrganisationId,
  signOut as authSignOut,
  loginWithEmail,
  signupWithEmail,
  signupWithOAuth,
  requestPasswordReset,
  updatePassword,
  CustomerAuthError,
  type OAuthProvider,
  type SignupWithEmailParams,
  type LoginWithEmailParams,
} from '@/lib/auth/customer-auth';
import { getVisitorId } from '@/lib/attribution/visitor-id';

// ============================================================================
// Context Types
// ============================================================================

interface CustomerAuthContextType {
  /** Customer profile from Route Royal */
  customer: CustomerProfile | null;
  /** Whether authentication is being checked */
  isLoading: boolean;
  /** Whether user is fully authenticated and provisioned (safe for API calls) */
  isAuthenticated: boolean;
  /** Whether user has a Supabase session (may not have customer profile yet) */
  hasSupabaseSession: boolean;
  /** Error if any */
  error: AuthError | null;
  
  // Auth operations
  signIn: (params: LoginWithEmailParams) => Promise<CustomerProfile | null>;
  signUp: (params: SignupWithEmailParams) => Promise<{ requiresEmailVerification: boolean }>;
  signInWithOAuth: (provider: OAuthProvider, redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string, captchaToken?: string) => Promise<void>;
  setNewPassword: (password: string) => Promise<void>;
  
  // Profile operations
  refreshProfile: () => Promise<void>;
  updateProfile: (data: Partial<CustomerProfile>) => Promise<CustomerProfile>;
  
  // API client access (for hooks)
  getCustomerClient: () => CustomerAPIClient | null;
  
  // Error handling
  clearError: () => void;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

interface CustomerAuthProviderProps {
  children: ReactNode;
  /** Optional organisation ID override (use TenantContext by default) */
  organisationId?: string;
}

export function CustomerAuthProvider({
  children,
  organisationId: propOrgId,
}: CustomerAuthProviderProps) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [hasSupabaseSession, setHasSupabaseSession] = useState(false);
  const [, setIsProvisioned] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Get organisation ID from TenantContext (primary) or prop override
  const tenantContext = useTenantOptional();
  const orgId = propOrgId || tenantContext?.organisationId || null;

  // Refs to avoid re-running the auth effect when orgId changes
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;
  
  // Track the last synced session to prevent duplicate syncs
  const lastSyncedSessionRef = useRef<string | null>(null);
  
  // Track if a sync is currently in progress (to prevent the delayed sync effect from looping)
  const isSyncInProgressRef = useRef(false);

  // Track the customer profile via ref so the main effect can check it without re-subscribing
  const customerRef = useRef(customer);
  customerRef.current = customer;

  // Track access token via ref so getCustomerClient always has the freshest token
  // (React state updates are batched; hooks may fire before state propagates)
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  // Helper: update both state and ref atomically so hooks get the token immediately
  const setAccessTokenWithRef = useCallback((token: string | null) => {
    accessTokenRef.current = token;
    setAccessToken(token);
  }, []);

  // Track previous pathname to detect navigation from exempt -> non-exempt pages
  const prevPathnameRef = useRef(pathname);

  // Check if Supabase is configured
  const isSupabaseConfigured = !!(
    process.env.NEXT_PUBLIC_SUPABASE_URL &&
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
  );

  // Set organisation ID in the auth module when it changes
  useEffect(() => {
    setOrganisationId(orgId);
  }, [orgId]);

  // Safety timeout: if isLoading is still true after 15 seconds, force it to false.
  // This prevents the UI from being stuck on a loading spinner indefinitely if the
  // auth flow hangs (e.g. network timeout, unexpected error path).
  useEffect(() => {
    if (!isLoading) return;
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('[CustomerAuthProvider] Loading timeout reached - forcing loading state to false');
        isSyncInProgressRef.current = false;
        setIsLoading(false);
      }
    }, 15000);
    return () => clearTimeout(timeout);
  }, [isLoading]);

  // Ensure the user has a UserOrganisation record in Route Royal.
  // The profile endpoint has a special guard bypass, so sync can succeed
  // even without org membership. Other endpoints (wishlists, bookings, etc.)
  // require it, so we call provisioning to create the record if missing.
  const ensureProvisioned = useCallback(async (
    session: { user: { id: string; email?: string; user_metadata?: Record<string, string>; app_metadata?: Record<string, string> } },
    currentOrgId: string,
  ) => {
    try {
      await fetch('/api/customer/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authProviderId: session.user.id,
          email: session.user.email,
          organisationId: currentOrgId,
          firstName: session.user.user_metadata?.first_name || session.user.user_metadata?.given_name,
          lastName: session.user.user_metadata?.last_name || session.user.user_metadata?.family_name,
          displayName: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
          avatarUrl: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture,
          authProvider: session.user.app_metadata?.provider || 'email',
          visitorId: getVisitorId(),
        }),
      });
    } catch {
      // Non-fatal: provisioning may already exist or endpoint may be unavailable
    }
  }, []);

  // Initialize auth state and listen for changes
  // IMPORTANT: This effect should only run once on mount. We use refs for orgId
  // to avoid re-subscribing when the tenant context resolves.
  useEffect(() => {
    if (!isSupabaseConfigured) {
      setIsLoading(false);
      return;
    }

    const supabase = createClient();
    let isMounted = true;

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, callbackSession) => {
        if (!isMounted) return;

        console.warn('[CustomerAuthProvider] onAuthStateChange:', event, !!callbackSession);

        const currentSession = callbackSession;

        // Use the ref to get the latest orgId without re-running the effect
        const currentOrgId = orgIdRef.current;

        if (event === 'SIGNED_OUT' || !currentSession) {
          console.warn('[CustomerAuthProvider] No session or signed out, clearing state');
          setCustomer(null);
          setIsProvisioned(false);
          setAccessTokenWithRef(null);
          setHasSupabaseSession(false);
          setIsLoading(false);
          lastSyncedSessionRef.current = null;
          return;
        }

        // Supabase fires auth events on every tab/window focus change:
        //  - TOKEN_REFRESHED: when the JWT is near expiry and gets refreshed
        //  - SIGNED_IN: Supabase's _recoverAndRefresh() re-notifies SIGNED_IN
        //    on every visibilitychange even when the session is still valid
        //
        // In both cases, if we already have a loaded customer profile we must
        // silently update the token ref and bail out — otherwise we'd trigger
        // setIsLoading(true) and the full sync flow on every tab switch, which
        // causes the page to flash a loading overlay.
        if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
          accessTokenRef.current = currentSession.access_token;
          if (customerRef.current || isSyncInProgressRef.current) {
            return;
          }
        }

        // User has a valid Supabase session — update state for events that
        // genuinely need it (INITIAL_SESSION, or SIGNED_IN / TOKEN_REFRESHED
        // when we still need to sync).
        setHasSupabaseSession(true);
        setAccessTokenWithRef(currentSession.access_token);

        // On MFA/auth pages, skip all processing - these pages handle their
        // own auth flow via server actions. Running client-side Supabase MFA
        // calls here can hang the browser.
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          const authFlowPaths = [
            '/setup-2fa', '/verify-2fa', '/account-recovery',
            '/login', '/register', '/forgot-password', '/reset-password',
            '/auth/callback',
          ];
          if (authFlowPaths.some(p => currentPath.includes(p))) {
            setIsLoading(false);
            return;
          }

          // MFA enforcement is handled by the middleware (server-side).
          // The middleware redirects to /verify-2fa or /setup-2fa if the
          // session is not at AAL2. By the time this client code runs, the
          // middleware has already validated the session. Performing a
          // redundant client-side MFA check here caused race conditions
          // with TOKEN_REFRESHED events and async Supabase calls that
          // could leave the auth state stuck in a loading state.
        }

        // Skip sync if we've already synced for this exact session (prevents duplicates)
        const sessionKey = `${currentSession.user.id}:${currentSession.access_token.substring(0, 20)}`;
        if (lastSyncedSessionRef.current === sessionKey) {
          setIsLoading(false);
          return;
        }

        const shouldSync = event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || 
          (event === 'TOKEN_REFRESHED' && !customerRef.current);
        
        if (shouldSync) {
          if (!currentOrgId) {
            return;
          }

          if (isSyncInProgressRef.current) {
            return;
          }
          isSyncInProgressRef.current = true;

          setIsLoading(true);
          let currentToken = currentSession.access_token;

          try {

            const syncResponse = await fetch('/api/customer/sync', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                accessToken: currentToken,
                organisationId: currentOrgId,
              }),
            });

            let responseText = await syncResponse.text();

            // If sync failed due to MFA/AAL enforcement, the token may still
            // carry AAL1 after a recent MFA verify. Refresh the session to
            // obtain an AAL2 token and retry once.
            if (!syncResponse.ok && responseText.includes('MFA verification required')) {
              const { data: refreshData } = await supabase.auth.refreshSession();
              if (refreshData?.session) {
                currentToken = refreshData.session.access_token;
                setAccessTokenWithRef(currentToken);

                const retryResponse = await fetch('/api/customer/sync', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    accessToken: currentToken,
                    organisationId: currentOrgId,
                  }),
                });
                responseText = await retryResponse.text();

                if (retryResponse.ok) {
                  const data = JSON.parse(responseText);
                  await ensureProvisioned(currentSession, currentOrgId);
                  if (isMounted) {
                    setIsProvisioned(true);
                    setCustomer(data.profile);
                    setError(null);
                    lastSyncedSessionRef.current = `${currentSession.user.id}:${currentToken.substring(0, 20)}`;
                  }
                  isSyncInProgressRef.current = false;
                  if (isMounted) setIsLoading(false);
                  return;
                }
              }
            }

            if (syncResponse.ok) {
              const data = JSON.parse(responseText);
              await ensureProvisioned(currentSession, currentOrgId);
              if (isMounted) {
                console.warn('[CustomerAuthProvider] Sync successful, setting customer profile');
                setIsProvisioned(true);
                setCustomer(data.profile);
                setError(null);
                lastSyncedSessionRef.current = sessionKey;
              }
            } else {
              console.warn('[CustomerAuthProvider] Sync failed:', syncResponse.status, responseText.substring(0, 200));
              throw new Error(`Sync failed: ${syncResponse.status} - ${responseText}`);
            }
          } catch {
            // Try to provision if sync fails (new customer for this org)
            try {
              if (currentOrgId) {
                const userData = currentSession.user;
                const provisionData = {
                  authProviderId: userData.id,
                  email: userData.email!,
                  organisationId: currentOrgId,
                  firstName: userData.user_metadata?.first_name || userData.user_metadata?.given_name,
                  lastName: userData.user_metadata?.last_name || userData.user_metadata?.family_name,
                  displayName: userData.user_metadata?.full_name || userData.user_metadata?.name,
                  avatarUrl: userData.user_metadata?.avatar_url || userData.user_metadata?.picture,
                  authProvider: userData.app_metadata?.provider || 'email',
                };
                
                const provisionResponse = await fetch('/api/customer/provision', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(provisionData),
                });

                if (!provisionResponse.ok) {
                  const errorData = await provisionResponse.json();
                  throw new Error(errorData.error || 'Provisioning failed');
                }

                // Try sync again after provisioning (use currentToken which may have been refreshed)
                const syncResponse = await fetch('/api/customer/sync', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    accessToken: currentToken,
                    organisationId: currentOrgId,
                  }),
                });

                if (syncResponse.ok) {
                  const { profile } = await syncResponse.json();
                  if (isMounted) {
                    setIsProvisioned(true);
                    setCustomer(profile);
                    setError(null);
                    lastSyncedSessionRef.current = sessionKey;
                  }
                } else {
                  throw new Error('Sync failed after provisioning');
                }
              }
            } catch (provisionErr) {
              const errMessage = provisionErr instanceof Error ? provisionErr.message : 'Unknown error';
              if (isMounted) {
                setError({
                  code: 'SYNC_FAILED',
                  message: `Failed to load customer profile: ${errMessage}`,
                });
              }
            }
          } finally {
            isSyncInProgressRef.current = false;
            if (isMounted) {
              setIsLoading(false);
            }
          }
        }
      }
    );

    // Check initial session - only used as a fallback to stop loading if
    // there truly is no session. If a sync is already in progress (started
    // by onAuthStateChange), we must not interfere.
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.warn('[CustomerAuthProvider] getSession result:', !!session, 'syncInProgress:', isSyncInProgressRef.current);
      if (!session && isMounted && !isSyncInProgressRef.current) {
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [isSupabaseConfigured]); // Only run once on mount. Uses refs for orgId and customer.

  // Effect to trigger sync when orgId becomes available after initial mount
  // NOTE: We intentionally exclude isLoading from deps to avoid infinite loops.
  // Instead, we use isSyncInProgressRef to prevent concurrent sync attempts.
  useEffect(() => {
    // Only run if we have orgId but no customer yet (meaning we skipped sync earlier)
    // Use the ref to prevent concurrent sync attempts instead of depending on isLoading
    if (orgId && hasSupabaseSession && !customer && !isSyncInProgressRef.current) {
      // Skip sync on MFA/auth pages - these pages handle their own auth flow
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const authFlowPaths = [
          '/setup-2fa', '/verify-2fa', '/account-recovery',
          '/login', '/register', '/forgot-password', '/reset-password',
          '/auth/callback',
        ];
        if (authFlowPaths.some(p => currentPath.includes(p))) {
          setIsLoading(false);
          return;
        }
      }

      const supabase = createClient();
      supabase.auth.getSession().then(async ({ data: { session } }) => {
        if (!session) {
          setIsLoading(false);
          return;
        }

        const currentSession = session;
        
        const sessionKey = `${currentSession.user.id}:${currentSession.access_token.substring(0, 20)}`;
        if (lastSyncedSessionRef.current === sessionKey) {
          // Mark loading as false if we're skipping due to already being synced
          setIsLoading(false);
          return;
        }

        // Prevent concurrent sync attempts
        if (isSyncInProgressRef.current) {
          return;
        }
        
        isSyncInProgressRef.current = true;
        setIsLoading(true);
        
        try {
          let currentToken = currentSession.access_token;

          const syncResponse = await fetch('/api/customer/sync', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: currentToken,
              organisationId: orgId,
            }),
          });

          if (syncResponse.ok) {
            const data = await syncResponse.json();
            await ensureProvisioned(currentSession, orgId);
            setIsProvisioned(true);
            setCustomer(data.profile);
            setError(null);
            lastSyncedSessionRef.current = sessionKey;
          } else {
            // If MFA enforcement rejected the token, refresh and retry once
            const errorText = await syncResponse.text().catch(() => '');
            if (errorText.includes('MFA verification required')) {
              const { data: refreshData } = await supabase.auth.refreshSession();
              if (refreshData?.session) {
                currentToken = refreshData.session.access_token;
                setAccessTokenWithRef(currentToken);

                const retryResponse = await fetch('/api/customer/sync', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    accessToken: currentToken,
                    organisationId: orgId,
                  }),
                });

                if (retryResponse.ok) {
                  const data = await retryResponse.json();
                  await ensureProvisioned(currentSession, orgId);
                  setIsProvisioned(true);
                  setCustomer(data.profile);
                  setError(null);
                  lastSyncedSessionRef.current = `${currentSession.user.id}:${currentToken.substring(0, 20)}`;
                  isSyncInProgressRef.current = false;
                  setIsLoading(false);
                  return;
                }
              }
            }
            // Mark this session as attempted even on failure to prevent infinite retries
            lastSyncedSessionRef.current = sessionKey;
          }
        } catch {
          // Mark as attempted to prevent infinite retries
          lastSyncedSessionRef.current = sessionKey;
        } finally {
          isSyncInProgressRef.current = false;
          setIsLoading(false);
        }
      });
    }
  }, [orgId, hasSupabaseSession, customer]); // Intentionally exclude isLoading to prevent loops

  // Effect to trigger sync when navigating from an MFA/auth page to a protected page.
  // After 2FA verification, the onAuthStateChange handler skips sync because the
  // pathname is still /verify-2fa. When router.push navigates to /account, the
  // deps of the other effects haven't changed, so nothing triggers sync. This
  // effect detects that transition and kicks off the sync.
  const MFA_EXEMPT_PATHS = [
    '/setup-2fa', '/verify-2fa', '/account-recovery',
    '/login', '/register', '/forgot-password', '/reset-password',
    '/auth/callback',
  ];

  useEffect(() => {
    const prevPath = prevPathnameRef.current;
    prevPathnameRef.current = pathname;

    const wasExempt = MFA_EXEMPT_PATHS.some(p => prevPath.includes(p));
    const isNowExempt = MFA_EXEMPT_PATHS.some(p => pathname.includes(p));

    // Only act when transitioning from an exempt page to a non-exempt page,
    // with a session but no customer profile yet.
    if (wasExempt && !isNowExempt && hasSupabaseSession && !customer && orgId && !isSyncInProgressRef.current) {
      isSyncInProgressRef.current = true;
      setIsLoading(true);

      const supabase = createClient();
      supabase.auth.getSession().then(async ({ data: { session } }) => {
        if (!session) {
          isSyncInProgressRef.current = false;
          setIsLoading(false);
          return;
        }

        // After MFA verification, refresh the session to ensure we have
        // the latest AAL2 token before syncing with Route Royal.
        let currentToken = session.access_token;
        try {
          const { data: refreshData } = await supabase.auth.refreshSession();
          if (refreshData?.session) {
            currentToken = refreshData.session.access_token;
            setAccessTokenWithRef(currentToken);
          }
        } catch {
          // Use existing token if refresh fails
        }

        try {
          const syncResponse = await fetch('/api/customer/sync', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken: currentToken,
              organisationId: orgId,
            }),
          });

          if (syncResponse.ok) {
            const data = await syncResponse.json();
            if (orgId) {
              await ensureProvisioned(session, orgId);
            }
            setIsProvisioned(true);
            setCustomer(data.profile);
            setError(null);
            lastSyncedSessionRef.current = `${session.user.id}:${currentToken.substring(0, 20)}`;
          } else {
            const errorText = await syncResponse.text().catch(() => '');
            setError({
              code: 'SYNC_FAILED',
              message: `Failed to load customer profile: ${errorText}`,
            });
          }
        } catch (err) {
          const errMessage = err instanceof Error ? err.message : 'Unknown error';
          setError({
            code: 'SYNC_FAILED',
            message: `Failed to load customer profile: ${errMessage}`,
          });
        } finally {
          isSyncInProgressRef.current = false;
          setIsLoading(false);
        }
      });
    }
  }, [pathname, hasSupabaseSession, customer, orgId]);

  // Auth operations
  const signIn = useCallback(async (params: LoginWithEmailParams): Promise<CustomerProfile | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const profile = await loginWithEmail(params);
      if (profile) {
        setCustomer(profile);
      }
      return profile;
    } catch (err) {
      const authError = err instanceof CustomerAuthError
        ? err.toAuthError()
        : { code: 'LOGIN_ERROR', message: err instanceof Error ? err.message : 'Login failed' };
      setError(authError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signUp = useCallback(async (params: SignupWithEmailParams) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signupWithEmail(params);
      
      if (!result.requiresEmailVerification) {
        router.refresh();
      }

      return { requiresEmailVerification: result.requiresEmailVerification };
    } catch (err) {
      const authError = err instanceof CustomerAuthError
        ? err.toAuthError()
        : { code: 'SIGNUP_ERROR', message: err instanceof Error ? err.message : 'Signup failed' };
      setError(authError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  const signInWithOAuth = useCallback(async (provider: OAuthProvider, redirectTo?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Determine the redirect path with locale
      // If redirectTo is provided, use it; otherwise default to /account
      // Ensure the path includes the current locale
      let targetPath = redirectTo || '/account';
      
      // Get current locale from URL path (e.g., /en/login -> en)
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const localeMatch = currentPath.match(/^\/([a-z]{2})(\/|$)/);
      const locale = localeMatch ? localeMatch[1] : 'en';
      
      // Ensure the target path has the locale prefix
      if (!targetPath.startsWith(`/${locale}`)) {
        // Remove any existing locale prefix first
        const pathWithoutLocale = targetPath.replace(/^\/[a-z]{2}(\/|$)/, '/');
        targetPath = `/${locale}${pathWithoutLocale === '/' ? '/account' : pathWithoutLocale}`;
      }
      
      const { url } = await signupWithOAuth(provider, targetPath);
      window.location.href = url;
    } catch (err) {
      const authError = err instanceof CustomerAuthError
        ? err.toAuthError()
        : { code: 'OAUTH_ERROR', message: err instanceof Error ? err.message : 'OAuth failed' };
      setError(authError);
      setIsLoading(false);
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    setIsLoading(true);

    try {
      await authSignOut();
      setCustomer(null);
      setIsProvisioned(false);
      setAccessTokenWithRef(null);
      router.push('/');
      router.refresh();
    } catch {
      // Sign out failed silently
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  const resetPassword = useCallback(async (email: string, captchaToken?: string) => {
    try {
      await requestPasswordReset(email, captchaToken);
    } catch (err) {
      const authError = err instanceof CustomerAuthError
        ? err.toAuthError()
        : { code: 'RESET_ERROR', message: err instanceof Error ? err.message : 'Reset failed' };
      throw authError;
    }
  }, []);

  const setNewPassword = useCallback(async (password: string) => {
    try {
      await updatePassword(password);
      router.push('/account');
    } catch (err) {
      const authError = err instanceof CustomerAuthError
        ? err.toAuthError()
        : { code: 'PASSWORD_ERROR', message: err instanceof Error ? err.message : 'Update failed' };
      throw authError;
    }
  }, [router]);

  // Profile operations
  const refreshProfile = useCallback(async () => {
    const token = accessTokenRef.current;
    const currentOrgId = orgIdRef.current;
    if (!token || !currentOrgId) return;

    try {
      const customerClient = createCustomerClient(currentOrgId);
      customerClient.setAccessToken(token);
      const profile = await customerClient.getProfile();
      setCustomer(profile);
    } catch {
      // Profile refresh failed silently
    }
  }, []);

  const updateProfile = useCallback(async (data: Partial<CustomerProfile>): Promise<CustomerProfile> => {
    const token = accessTokenRef.current;
    const currentOrgId = orgIdRef.current;
    if (!token || !currentOrgId) {
      throw new Error('Not authenticated');
    }

    const response = await fetch('/api/customer/profile', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accessToken: token,
        organisationId: currentOrgId,
        profileData: data,
      }),
    });

    const responseData = await response.json();
    
    if (!response.ok) {
      throw new Error(responseData.error || 'Failed to update profile');
    }

    setCustomer(responseData.profile);
    return responseData.profile;
  }, [accessToken, orgId]);

  // Get customer client for hooks - uses ref for the freshest token
  const getCustomerClient = useCallback((): CustomerAPIClient | null => {
    const token = accessTokenRef.current;
    const currentOrgId = orgIdRef.current;
    if (!token || !currentOrgId) return null;

    const client = createCustomerClient(currentOrgId);
    client.setAccessToken(token);
    return client;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: CustomerAuthContextType = {
    customer,
    isLoading,
    isAuthenticated: !!customer,
    hasSupabaseSession,
    error,
    signIn,
    signUp,
    signInWithOAuth,
    signOut,
    resetPassword,
    setNewPassword,
    refreshProfile,
    updateProfile,
    getCustomerClient,
    clearError,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access customer auth context
 */
export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);

  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }

  return context;
}

// Export types
export type { CustomerAuthContextType, OAuthProvider, LoginWithEmailParams, SignupWithEmailParams };
